/**
 * ConnectionIndicator Component
 *
 * Displays last connected time with a colored dot indicator.
 * Green = connected recently (within 1 hour)
 * Yellow = connected within 7 days
 * Red = not connected for more than 7 days
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { colors, typography as typographyTokens } from '../../tokens';

dayjs.extend(relativeTime);

// Thresholds in milliseconds
const ONE_HOUR = 60_000 * 60;
const SEVEN_DAYS = 60_000 * 60 * 24 * 7;

export interface ConnectionIndicatorProps {
    /** ISO timestamp string */
    timestamp: string | null | undefined;
}

const getConnectionStatus = (
    difference: number | null
): { color: string; label: string } => {
    if (difference === null) {
        return { color: colors.neutral[400], label: 'Unknown' };
    }

    if (difference < ONE_HOUR) {
        return { color: colors.success.main, label: 'Just now' };
    }

    if (difference < SEVEN_DAYS) {
        return { color: colors.warning.main, label: '' }; // Will use relative time
    }

    return { color: colors.error.main, label: '' }; // Will use relative time
};

export const ConnectionIndicator: React.FC<ConnectionIndicatorProps> = ({
    timestamp,
}) => {
    const difference = timestamp ? Date.now() - new Date(timestamp).getTime() : null;
    const { color, label } = getConnectionStatus(difference);
    const displayText = label || (timestamp ? dayjs().to(timestamp) : 'Unknown');

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                }}
            />
            <Typography
                sx={{
                    fontSize: typographyTokens.fontSize.sm,
                    color: color,
                    fontWeight: typographyTokens.fontWeight.medium,
                }}
            >
                {displayText}
            </Typography>
        </Box>
    );
};

export default ConnectionIndicator;

